import { BoundingBox, Color, Mat4, Quat, Vec3 } from 'playcanvas';

import { AnimTrack } from './anim-track';
import { Element } from './element';
import { Group } from './group';
import { IndexRanges, sortedPredicate } from './index-ranges';
import { Pivot } from './pivot';
import { Scene } from './scene';
import { Splat } from './splat';
import { State } from './splat-state';
import { Transform } from './transform';

interface EditOp {
    name: string;
    do(): void | Promise<void>;
    undo(): void | Promise<void>;
    destroy?(): void;
}

const enum BitOp {
    SET,
    CLEAR,
    TOGGLE
}

class StateOp {
    splat: Splat;
    ranges: IndexRanges;
    mask: number;
    op: BitOp;
    updateFlags: number;

    constructor(splat: Splat, ranges: IndexRanges, mask: number, op: BitOp, updateFlags = State.selected) {
        this.splat = splat;
        this.ranges = ranges;
        this.mask = mask;
        this.op = op;
        this.updateFlags = updateFlags;
    }

    private apply(op: BitOp) {
        const state = this.splat.splatData.getProp('state') as Uint8Array;
        const { mask } = this;

        switch (op) {
            case BitOp.SET:
                this.ranges.forEach((i) => {
                    state[i] |= mask;
                });
                break;
            case BitOp.CLEAR:
                this.ranges.forEach((i) => {
                    state[i] &= ~mask;
                });
                break;
            case BitOp.TOGGLE:
                this.ranges.forEach((i) => {
                    state[i] ^= mask;
                });
                break;
        }
    }

    async do() {
        this.apply(this.op);
        await this.splat.updateState(this.updateFlags);
    }

    async undo() {
        const undoOp = this.op === BitOp.TOGGLE ? BitOp.TOGGLE :
            this.op === BitOp.SET ? BitOp.CLEAR : BitOp.SET;
        this.apply(undoOp);
        await this.splat.updateState(this.updateFlags);
    }

    destroy() {
        this.splat = null;
        this.ranges = null;
    }
}

class SelectAllOp extends StateOp {
    name = 'selectAll';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === 0), State.selected, BitOp.SET);
    }
}

class SelectNoneOp extends StateOp {
    name = 'selectNone';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.selected, BitOp.CLEAR);
    }
}

class SelectInvertOp extends StateOp {
    name = 'selectInvert';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & (State.locked | State.deleted)) === 0), State.selected, BitOp.TOGGLE);
    }
}

class SelectOp extends StateOp {
    name = 'selectOp';

    constructor(splat: Splat, op: 'add' | 'remove' | 'set', filter: ((i: number) => boolean) | Uint32Array) {
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const bitOp = op === 'add' ? BitOp.SET : op === 'remove' ? BitOp.CLEAR : BitOp.TOGGLE;

        // wrap sorted IDs in a cursor-based predicate
        const pred = filter instanceof Uint32Array ? sortedPredicate(filter) : filter;

        const preds = {
            add: (i: number) => pred(i) && state[i] === 0,
            remove: (i: number) => pred(i) && state[i] === State.selected,
            set: (i: number) => (state[i] === State.selected) !== pred(i)
        };

        super(splat, IndexRanges.fromPredicate(splatData.numSplats, preds[op]), State.selected, bitOp);
    }
}

class HideSelectionOp extends StateOp {
    name = 'hideSelection';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.locked, BitOp.SET, State.locked);
    }
}

class UnhideAllOp extends StateOp {
    name = 'unhideAll';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & (State.locked | State.deleted)) === State.locked), State.locked, BitOp.CLEAR, State.locked);
    }
}

class DeleteSelectionOp extends StateOp {
    name = 'deleteSelection';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.deleted, BitOp.SET, State.deleted);
    }
}

class ResetOp extends StateOp {
    name = 'reset';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & State.deleted) !== 0), State.deleted, BitOp.CLEAR, State.deleted);
    }
}

// op for modifying a splat transform
class EntityTransformOp {
    name = 'entityTransform';
    splat: Splat;
    oldt: Transform;
    newt: Transform;

    constructor(options: { splat: Splat, oldt: Transform, newt: Transform }) {
        this.splat = options.splat;
        this.oldt = options.oldt;
        this.newt = options.newt;
    }

    do() {
        this.splat.move(this.newt.position, this.newt.rotation, this.newt.scale);
    }

    undo() {
        this.splat.move(this.oldt.position, this.oldt.rotation, this.oldt.scale);
    }

    destroy() {
        this.splat = null;
        this.oldt = null;
        this.newt = null;
    }
}

const mat = new Mat4();

// op for modifying a subset of individual splats
class SplatsTransformOp {
    name = 'splatsTransform';

    splat: Splat;
    transform: Mat4;
    paletteMap: Map<number, number>;

    constructor(options: { splat: Splat, transform: Mat4, paletteMap: Map<number, number> }) {
        this.splat = options.splat;
        this.transform = options.transform;
        this.paletteMap = options.paletteMap;
    }

    async do() {
        const { splat, transform, paletteMap } = this;
        const state = splat.splatData.getProp('state') as Uint8Array;
        const indices = splat.transformTexture.lock() as Uint16Array;

        // update splat transform palette indices
        for (let i = 0; i < state.length; ++i) {
            if (state[i] === State.selected) {
                indices[i] = paletteMap.get(indices[i]);
            }
        }

        splat.transformTexture.unlock();

        splat.transformPalette.alloc(paletteMap.size);

        // update transform palette
        const { transformPalette } = splat;
        this.paletteMap.forEach((newIdx, oldIdx) => {
            transformPalette.getTransform(oldIdx, mat);
            mat.mul2(transform, mat);
            transformPalette.setTransform(newIdx, mat);
        });

        await splat.updatePositions();
    }

    async undo() {
        const { splat, paletteMap } = this;
        const state = splat.splatData.getProp('state') as Uint8Array;
        const indices = splat.transformTexture.lock() as Uint16Array;

        // invert the palette map
        const inverseMap = new Map<number, number>();
        paletteMap.forEach((newIdx, oldIdx) => {
            inverseMap.set(newIdx, oldIdx);
        });

        // restore the original transform indices
        for (let i = 0; i < state.length; ++i) {
            if (state[i] === State.selected) {
                indices[i] = inverseMap.get(indices[i]);
            }
        }

        splat.transformTexture.unlock();

        splat.transformPalette.free(paletteMap.size);

        await splat.updatePositions();
    }

    destroy() {
        this.splat = null;
        this.transform = null;
        this.paletteMap = null;
    }
}

class PlacePivotOp {
    name = 'setPivot';
    pivot: Pivot;
    oldt: Transform;
    newt: Transform;

    constructor(options: { pivot: Pivot, oldt: Transform, newt: Transform }) {
        this.pivot = options.pivot;
        this.oldt = options.oldt;
        this.newt = options.newt;
    }

    do() {
        this.pivot.place(this.newt);
    }

    undo() {
        this.pivot.place(this.oldt);
    }
}

type ColorAdjustment = {
    tintClr?: Color
    temperature?: number,
    saturation?: number,
    brightness?: number,
    blackPoint?: number,
    whitePoint?: number,
    transparency?: number
};

class SetSplatColorAdjustmentOp {
    name: 'setSplatColor';
    splat: Splat;

    newState: ColorAdjustment;
    oldState: ColorAdjustment;

    constructor(options: { splat: Splat, oldState: ColorAdjustment, newState: ColorAdjustment }) {
        const { splat, oldState, newState } = options;
        this.splat = splat;
        this.oldState = oldState;
        this.newState = newState;
    }

    do() {
        const { splat } = this;
        const { tintClr, temperature, saturation, brightness, blackPoint, whitePoint, transparency } = this.newState;
        if (tintClr) splat.tintClr = tintClr;
        if (temperature !== null) splat.temperature = temperature;
        if (saturation !== null) splat.saturation = saturation;
        if (brightness !== null) splat.brightness = brightness;
        if (blackPoint !== null) splat.blackPoint = blackPoint;
        if (whitePoint !== null) splat.whitePoint = whitePoint;
        if (transparency !== null) splat.transparency = transparency;
    }

    undo() {
        const { splat } = this;
        const { tintClr, temperature, saturation, brightness, blackPoint, whitePoint, transparency } = this.oldState;
        if (tintClr) splat.tintClr = tintClr;
        if (temperature !== null) splat.temperature = temperature;
        if (saturation !== null) splat.saturation = saturation;
        if (brightness !== null) splat.brightness = brightness;
        if (blackPoint !== null) splat.blackPoint = blackPoint;
        if (whitePoint !== null) splat.whitePoint = whitePoint;
        if (transparency !== null) splat.transparency = transparency;
    }
}

// Snapshot-based undo/redo for animation track edits.
// Captures the full track state before and after a mutation.
class AnimTrackEditOp {
    name: string;
    track: AnimTrack;
    before: unknown;
    after: unknown;

    constructor(name: string, track: AnimTrack, before: unknown, after: unknown) {
        this.name = name;
        this.track = track;
        this.before = before;
        this.after = after;
    }

    do() {
        this.track.restore(this.after);
    }

    undo() {
        this.track.restore(this.before);
    }
}

class MultiOp {
    name = 'multiOp';
    ops: EditOp[];

    constructor(ops: EditOp[]) {
        this.ops = ops;
    }

    async do() {
        for (const op of this.ops) {
            await op.do();
        }
    }

    async undo() {
        for (const op of this.ops) {
            await op.undo();
        }
    }
}

class AddSplatOp {
    name: 'addSplat';
    scene: Scene;
    splat: Splat;

    constructor(scene: Scene, splat: Splat) {
        this.scene = scene;
        this.splat = splat;
    }

    async do() {
        await this.scene.add(this.splat);
    }

    undo() {
        this.scene.remove(this.splat);
    }

    destroy() {
        this.splat.destroy();
    }
}

// generic add/remove ops for any scene Element (used for content objects —
// image/text/shape primitives — so placing or deleting one is undoable). Kept
// separate from AddSplatOp (property name `element` not `splat`) so
// EditHistory.removeForSplat's splat-specific duck-typing check never matches
// these by accident.
class AddElementOp {
    name = 'addElement';
    scene: Scene;
    element: Element;

    constructor(scene: Scene, element: Element) {
        this.scene = scene;
        this.element = element;
    }

    async do() {
        await this.scene.add(this.element);
    }

    undo() {
        this.scene.remove(this.element);
    }

    destroy() {
        this.element.destroy?.();
    }
}

class RemoveElementOp {
    name = 'removeElement';
    scene: Scene;
    element: Element;

    constructor(scene: Scene, element: Element) {
        this.scene = scene;
        this.element = element;
    }

    do() {
        this.scene.remove(this.element);
    }

    async undo() {
        await this.scene.add(this.element);
    }
}

// Move elements to a new parent, preserving world transforms so nothing appears to jump
// when it is dragged into or out of a group. The transform maths is delegated to
// Element.getWorldTransformComponents/moveWorld (Task 2) rather than repeated here, so
// the exact-passthrough early-out for root-parented elements and the mirrored-scale
// handling stay in one place.
class ReparentOp {
    name = 'reparent';

    scene: Scene;
    items: { element: Element, oldParent: Group | null, newParent: Group | null }[];

    constructor(options: { scene: Scene, elements: Element[], parent: Group | null }) {
        this.scene = options.scene;
        this.items = options.elements.map(element => ({
            element,
            oldParent: element.parent,
            newParent: options.parent
        }));
    }

    private apply(getParent: (item: { oldParent: Group | null, newParent: Group | null }) => Group | null) {
        this.items.forEach((item) => {
            const { element } = item;

            // capture the world transform before the entity moves in the graph
            const position = new Vec3();
            const rotation = new Quat();
            const scale = new Vec3();
            element.getWorldTransformComponents(position, rotation, scale);

            this.scene.detachEntity(element);
            element.parent = getParent(item);
            this.scene.attachEntity(element);

            // restore the world transform under the new parent
            element.moveWorld(position, rotation, scale);
        });

        // the moved elements (and everything below them) now have a different ancestor
        // chain, so their inherited visibility may have changed
        this.scene.elements.forEach((e) => {
            (e as any).updateEffectiveVisibility?.();
        });

        this.scene.boundDirty = true;
        this.scene.events.fire('scene.hierarchyChanged');
    }

    do() {
        this.apply(item => item.newParent);
    }

    undo() {
        this.apply(item => item.oldParent);
    }
}

// Create a group and move the current selection into it. The group's transform is placed
// at the centre of the selection's combined bound so later rotation and scaling behave
// intuitively rather than pivoting around the world origin.
class GroupCreateOp {
    name = 'groupCreate';

    scene: Scene;
    group: Group;
    elements: Element[];
    parent: Group | null;
    reparentOp: ReparentOp = null;
    centre: Vec3;

    constructor(options: { scene: Scene, elements: Element[], name?: string }) {
        this.scene = options.scene;
        this.group = new Group(options.name ?? 'Group');
        this.elements = options.elements.slice();
        this.parent = options.elements[0]?.parent ?? null;

        // Group.worldBound hands back shared per-group storage that the next call mutates,
        // so every bound is copied into locally owned storage and never retained.
        const bound = new BoundingBox();
        let valid = false;
        this.elements.forEach((e) => {
            const b = e.worldBound;
            if (!b) {
                return;
            }
            if (valid) {
                bound.add(b);
            } else {
                bound.copy(b);
                valid = true;
            }
        });
        this.centre = valid ? bound.center.clone() : new Vec3();
    }

    async do() {
        this.group.parent = this.parent;
        await this.scene.add(this.group);

        // the accumulated centre is a world-space point; moveWorld converts through the
        // parent's inverse when the new group is itself nested, and passes straight
        // through to move() when it sits at the scene root
        this.group.moveWorld(this.centre);

        if (!this.reparentOp) {
            this.reparentOp = new ReparentOp({
                scene: this.scene,
                elements: this.elements,
                parent: this.group
            });
        }
        this.reparentOp.do();
    }

    undo() {
        this.reparentOp.undo();
        this.scene.remove(this.group);
    }

    // only called for ops discarded from the redo branch, i.e. ops that are currently
    // undone - at which point the group is out of the scene and safe to destroy
    destroy() {
        this.group.destroy();
    }
}

// Move a group's direct children up to the group's own parent, then remove the group.
// Nested subgroups move as units; only one level of nesting is dissolved.
class UngroupOp {
    name = 'ungroup';

    scene: Scene;
    group: Group;
    children: Element[];
    reparentOp: ReparentOp;

    constructor(options: { scene: Scene, group: Group }) {
        this.scene = options.scene;
        this.group = options.group;
        this.children = options.scene.elements.filter(e => e.parent === options.group);
        this.reparentOp = new ReparentOp({
            scene: options.scene,
            elements: this.children,
            parent: options.group.parent
        });
    }

    do() {
        this.reparentOp.do();
        this.scene.remove(this.group);
    }

    async undo() {
        await this.scene.add(this.group);
        this.reparentOp.undo();
    }
}

class GroupRenameOp {
    name = 'groupRename';

    group: Group;
    oldName: string;
    newName: string;

    constructor(group: Group, newName: string) {
        this.group = group;
        this.oldName = group.name;
        this.newName = newName;
    }

    do() {
        this.group.name = this.newName;
    }

    undo() {
        this.group.name = this.oldName;
    }
}

class SplatRenameOp {
    name = 'splatRename';
    splat: Splat;
    oldName: string;
    newName: string;

    constructor(splat: Splat, newName: string) {
        this.splat = splat;
        this.oldName = splat.name;
        this.newName = newName;
    }

    do() {
        this.splat.name = this.newName;
    }

    undo() {
        this.splat.name = this.oldName;
    }
}

export {
    EditOp,
    SelectAllOp,
    SelectNoneOp,
    SelectInvertOp,
    SelectOp,
    HideSelectionOp,
    UnhideAllOp,
    DeleteSelectionOp,
    ResetOp,
    EntityTransformOp,
    SplatsTransformOp,
    PlacePivotOp,
    ColorAdjustment,
    SetSplatColorAdjustmentOp,
    AnimTrackEditOp,
    MultiOp,
    AddSplatOp,
    AddElementOp,
    RemoveElementOp,
    SplatRenameOp,
    ReparentOp,
    GroupCreateOp,
    UngroupOp,
    GroupRenameOp
};
